import type { Data } from '@strapi/types';
import { useTranslations } from '@power-core/toolkit';
import type { FunctionComponent } from 'react';
import { useAppContext } from '@/modules/app-context/use-app-context';
import { useDocumentApi } from '@/modules/document/hooks/use-document-api';
import { generateRedirectUrl } from '@/modules/utils/redirect-url';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { History } from '@/ui-components/history/history';
import { LoadingIndicator } from '@/ui-components/loading-indicator/loading-indicator';
import { Paragraph } from '@/ui-components/paragraph/paragraph';

import type { VersionsListProps } from './types';
import { useAuth } from '@power-core/toolkit';

type OnHistoryItemClick = {
	documentType: string;
	documentId: string;
	documentFileId: string;
	isLatest: boolean;
	region?: Data.Entity<'api::project-document.project-document'>['region'];
};

export const VersionsList: FunctionComponent<VersionsListProps> = ({ isLoading, error, historyItems }) => {
	const t = useTranslations();
	const { auth } = useAuth();
	const { project, appConfig, documentTypeURLS } = useAppContext();
	const { openPreviousVersion } = useDocumentApi();

	const onClick = async ({ documentType, documentId, documentFileId, isLatest, region }: OnHistoryItemClick) => {
		if (!project?.documentId) throw new Error('Project document ID not found');

		let url = '';

		if (documentTypeURLS) {
			url = documentTypeURLS[documentType];
		}

		if (!url) throw new Error(`Unsupported document type: ${documentType}`);

		if (isLatest) {
			return generateRedirectUrl({
				url,
				documentId,
				toolUrl: appConfig.appName,
				user: auth?.data?.user?.email ?? '',
				...(documentType === 'T_CURVES' && region ? { region } : {}),
			});
		}

		openPreviousVersion
			.mutateAsync({
				projectDocumentId: documentId,
				documentFileId,
			})
			.then((response) => {
				const { fileName, projectDocumentId } = response.data;

				if (!documentId || !projectDocumentId) throw new Error('Document ID not found');
				if (!fileName) throw new Error('File name was not found');
				generateRedirectUrl({
					url,
					documentId: projectDocumentId,
					toolUrl: appConfig.appName,
					user: auth?.data?.user?.email ?? '',
					...(documentType === 'T_CURVES' && region ? { region } : {}),
				});
			})
			.catch((error) => {
				console.error(error);
			});
	};

	if (isLoading) {
		return (
			<Flexbox justify='center' className='mt-5'>
				<LoadingIndicator sizing='large' color='primary' />
			</Flexbox>
		);
	}

	if (error) {
		return (
			<Paragraph variant='text-base-medium' className='text-center pt-2'>
				{t('T_PROJECT_HISTORY_FETCHING_ERROR')}
			</Paragraph>
		);
	}

	if (!historyItems?.length) {
		return (
			<Paragraph variant='text-base-medium' className='text-center pt-2'>
				{t('T_NO_HISTORY')}
			</Paragraph>
		);
	}

	return (
		<Flexbox className='absolute mt-[132px] h-[calc(100%-142px)] xx-large:h-[calc(100%-92px)] overflow-auto w-full scrollbar-rounded xx-large:mt-[92px]'>
			<History
				historyItems={historyItems}
				onHistoryItemAction={(item) => {
					onClick({
						documentFileId: item.documentFile?.documentId ?? '',
						documentType: item.documentType ?? '',
						isLatest: item.documentFile?.isLatest === true,
						documentId: item.documentId ?? '',
						region: item.region,
					});
				}}
				titleVariant='text-sm-semibold'
				timeVariant='text-xs'
			/>
		</Flexbox>
	);
};
